import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Table2 } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { useSchemaStore } from '@/frontend/store/SchemaStore';
import { LoadingSpinner } from '../../LoadingSpinner';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Textarea } from '../../ui/textarea';
import { PublishedCell, StatusDot } from '../studioChrome';

/** Every schema — the input sheets report bundles read — with its latest version and who reads it, and the way to make one. */
export function SchemaList() {
  const schemas = useSchemaStore((s) => s.schemas);
  const fetchSchemas = useSchemaStore((s) => s.fetchSchemas);
  const [showCreate, setShowCreate] = useState(false);

  // a list read on an earlier visit is read again: saving or publishing a bundle changes its schema too
  useEffect(() => {
    if (useSchemaStore.getState().schemas !== null) void fetchSchemas();
  }, [fetchSchemas]);
  useEffect(() => {
    if (schemas === null) void fetchSchemas();
  }, [schemas, fetchSchemas]);

  return (
    <section className="flex flex-col gap-3" aria-labelledby="schemas-heading">
      <div className="flex items-start justify-between gap-6">
        <div className="flex flex-col gap-1">
          <h2 id="schemas-heading" className="flex items-center gap-2 text-base font-semibold"><Table2 className="size-4 text-muted-foreground" /> {I18n.get('schemas')}</h2>
          <p className="max-w-2xl text-muted-foreground">{I18n.get('schemasHelp')}</p>
        </div>
        <Button variant="outline" className="shrink-0" onClick={() => setShowCreate(true)}><Plus /> {I18n.get('newSchema')}</Button>
      </div>
      <div className="overflow-hidden rounded-xl border-[1px] bg-background">
        {schemas === null ? (
          <div className="flex justify-center py-10" aria-label={I18n.get('loading')}><LoadingSpinner /></div>
        ) : schemas.length === 0 ? (
          <p className="px-4 py-10 text-center text-muted-foreground">{I18n.get('schemaNone')}</p>
        ) : (
          <Table>
            <TableHeader className="bg-muted/40">
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-2/5 pl-4">{I18n.get('reportBundleTitle')}</TableHead>
                <TableHead>{I18n.get('reportBundlePublishedColumn')}</TableHead>
                <TableHead>{I18n.get('schemaReadBy')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {schemas.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="min-w-[10rem] pl-4 whitespace-normal">
                    {/* a dot only when something is wrong: a draft that is a sheet is the usual case */}
                    <div className="flex items-start gap-2.5">
                      <StatusDot tone={s.draftOk ? 'idle' : 'bad'} className={s.draftOk ? 'invisible mt-1.5 size-2' : 'mt-1.5 size-2'} />
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <Link to={`/report-studio/schemas/${s.id}`} className="font-medium text-foreground [overflow-wrap:anywhere] hover:text-primary hover:underline">{s.title}</Link>
                        {!s.draftOk && <span className="text-xs text-destructive">{I18n.get('schemaDraftProblems')}</span>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <PublishedCell version={s.latestVersion} changed={s.draftChanged} />
                  </TableCell>
                  <TableCell className="min-w-[10rem] whitespace-normal [overflow-wrap:anywhere]">
                    {s.usedBy.length
                      ? s.usedBy.map((u, i) => <span key={u.reportBundleId}><Link to={`/report-studio/${u.reportBundleId}`} className="text-primary hover:underline">{u.title}</Link>{i < s.usedBy.length - 1 ? ', ' : ''}</span>)
                      : <span className="text-muted-foreground">{I18n.get('schemaReadByNone')}</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      <Dialog open={showCreate} onOpenChange={setShowCreate}>{showCreate && <CreateSchemaDialog onDone={() => setShowCreate(false)} />}</Dialog>
    </section>
  );
}

function CreateSchemaDialog({ onDone }: { onDone: () => void }) {
  const createSchema = useSchemaStore((s) => s.createSchema);
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const create = async () => {
    setSaving(true);
    try {
      const schema = await createSchema({ title: title.trim(), description: description.trim() });
      if (schema) {
        onDone();
        void navigate(`/report-studio/schemas/${schema.id}`);
      }
    } finally {
      setSaving(false);
    }
  };
  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{I18n.get('newSchema')}</DialogTitle>
        <DialogDescription>{I18n.get('newSchemaHelp')}</DialogDescription>
      </DialogHeader>
      <div className="grid gap-2">
        <Label htmlFor="new-schema-title">{I18n.get('reportBundleTitle')}</Label>
        <Input id="new-schema-title" value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="new-schema-description">{I18n.get('reportBundleDescription')}</Label>
        <Textarea id="new-schema-description" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onDone} disabled={saving}>{I18n.get('cancel')}</Button>
        <Button onClick={() => void create()} disabled={saving || title.trim() === ''}>{saving ? I18n.get('saving') : I18n.get('create')}</Button>
      </DialogFooter>
    </DialogContent>
  );
}
